import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input, Label, Textarea } from "@bookdental/ui";
import { BRAZILIAN_STATES } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { TabSaveBar } from "@/components/profile/TabSaveBar";
import { SeletorDeEspecialidades } from "@/components/services/SeletorDeEspecialidades";
import { useRecarregarPerfil } from "./use-recarregar-perfil";

const LIMITE_DA_BIO = 500;
const rotulo = "text-[11px] font-medium uppercase tracking-wider text-zinc-500";

/** Nome, CRO, especialidades e biografia — um formulário só, um botão de salvar. */
export function DadosProfissionaisSecao() {
  const user = trpc.me.current.useQuery().data;
  const opcoes = trpc.specialty.list.useQuery().data ?? [];
  const recarregar = useRecarregarPerfil();
  const [nome, setNome] = useState("");
  const [cro, setCro] = useState("");
  const [uf, setUf] = useState("");
  const [especialidades, setEspecialidades] = useState<string[]>([]);
  const [bio, setBio] = useState("");

  useEffect(() => {
    if (!user) return;
    setNome(user.name ?? "");
    setCro(user.croNumber ?? "");
    setUf(user.croState ?? "");
    setEspecialidades(user.specialtyIds);
    setBio(user.bio ?? "");
  }, [user]);

  const perfil = trpc.me.updateProfile.useMutation();
  const tags = trpc.me.updateSpecialties.useMutation();
  if (!user) return null;

  const mudouCro = cro !== (user.croNumber ?? "") || uf !== (user.croState ?? "");
  const mudouTags = [...especialidades].sort().join() !== [...user.specialtyIds].sort().join();
  const mudouResto = nome !== user.name || bio !== (user.bio ?? "");
  const croValido = (!cro && !uf) || (/^\d{3,8}$/.test(cro) && uf.length === 2);
  const valido = nome.trim().length >= 2 && croValido;

  const salvar = async () => {
    try {
      await Promise.all([
        mudouResto || mudouCro
          ? perfil.mutateAsync({
              ...(mudouResto ? { name: nome.trim(), bio } : {}),
              // CRO só vai quando muda: mandar de novo devolveria o status pra pendente.
              ...(mudouCro ? { croNumber: cro || undefined, croState: (uf || undefined) as never } : {}),
            })
          : null,
        mudouTags ? tags.mutateAsync({ specialtyIds: especialidades }) : null,
      ]);
      toast.success(mudouCro ? "Dados salvos — o CRO volta a ficar pendente de verificação." : "Dados salvos.");
      recarregar();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Não foi possível salvar.");
    }
  };

  return (
    <div className="space-y-5 px-6 py-5">
      <div className="grid gap-4 sm:grid-cols-12">
        <div className="space-y-1.5 sm:col-span-12">
          <Label htmlFor="dp-nome" className={rotulo}>Nome completo</Label>
          <Input id="dp-nome" value={nome} onChange={(e) => setNome(e.target.value)} className="h-11" />
        </div>
        <div className="space-y-1.5 sm:col-span-8">
          <Label htmlFor="dp-cro" className={rotulo}>CRO</Label>
          <Input id="dp-cro" value={cro} onChange={(e) => setCro(e.target.value.replace(/\D/g, ""))} placeholder="12345" inputMode="numeric" className="h-11" />
        </div>
        <div className="space-y-1.5 sm:col-span-4">
          <Label htmlFor="dp-uf" className={rotulo}>UF</Label>
          <select
            id="dp-uf"
            value={uf}
            onChange={(e) => setUf(e.target.value)}
            className="flex h-11 w-full rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring dark:bg-card"
          >
            <option value="">—</option>
            {BRAZILIAN_STATES.map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
        </div>
      </div>
      {mudouCro && <p className="text-[11px] text-amber-700">Alterar o CRO volta o status para pendente até a verificação.</p>}

      <div className="space-y-2">
        <span className={rotulo}>Especialidades</span>
        <SeletorDeEspecialidades
          principais={opcoes.filter((e) => e.destaque)}
          outras={opcoes.filter((e) => !e.destaque)}
          marcadas={especialidades}
          onChange={setEspecialidades}
          rotuloRecolher="Mostrar só as principais"
        />
        <p className="text-[11px] text-zinc-500">São suas — não mudam os procedimentos de nenhuma clínica.</p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="dp-bio" className={rotulo}>Biografia profissional</Label>
        <Textarea
          id="dp-bio"
          value={bio}
          maxLength={LIMITE_DA_BIO}
          onChange={(e) => setBio(e.target.value)}
          rows={4}
          placeholder="Uma breve descrição sobre você e sua atuação."
        />
        <p className="text-right text-[11px] tabular-nums text-zinc-400">{bio.length}/{LIMITE_DA_BIO}</p>
      </div>

      <TabSaveBar dirty={(mudouResto || mudouCro || mudouTags) && valido} avisar={mudouResto || mudouCro || mudouTags} isPending={perfil.isPending || tags.isPending} onSave={salvar} />
    </div>
  );
}
